/**
 * AdoraCare — Walk-in form data shapes (v1 spine).
 *
 * Source vocabulary (8 categorical buckets, aligned with backend
 * lib/walkin-handler.ts and DB CHECK in walkins.source):
 *   paid_search | paid_social | organic_listing | messaging |
 *   referral    | walk_in     | phone_call      | other
 *
 * The form never sends organizationId or receptionistPhoneFp — both
 * are derived server-side. selectedContactId may be set when the
 * receptionist confirmed a returning patient via /api/walkins/lookup.
 */

export type WalkinSource =
  | 'paid_search'
  | 'paid_social'
  | 'organic_listing'
  | 'messaging'
  | 'referral'
  | 'walk_in'
  | 'phone_call'
  | 'other';

export type WalkinSourceConfidence =
  | 'patient_stated'
  | 'system_attributed'
  | 'inferred'
  | 'unknown';

export type NewPatientStatus = 'new' | 'returning' | 'unknown';

/**
 * Identifier for a source tile shown in the form. Each tile maps
 * to a (WalkinSource, sourceDetail) pair via validation.ts.
 */
export type SourceTileId =
  | 'google'
  | 'meta'
  | 'walk_in'
  | 'referral'
  | 'whatsapp'
  | 'justdial'
  | 'practo'
  | 'other';

export interface WalkinFormData {
  clinicId: number | null;
  patientName: string;
  phoneRaw: string;
  patientAge: number | null;
  source: WalkinSource | '';
  sourceDetail?: string;
  sourceConfidence: WalkinSourceConfidence;
  patientIsNew: boolean | null;
  referringDoctorName?: string;
}

export interface WalkinPostBody extends WalkinFormData {
  submittedByPhone: string;
  /**
   * Present only when a returning patient in the same organization
   * was selected via /api/walkins/lookup. The backend re-validates
   * that the chosen contact's organization_id matches the resolved
   * organization of the submitted clinicId and that the phone
   * fingerprint matches the typed phone, then reuses the existing
   * contact row instead of inserting a new one.
   */
  selectedContactId?: number;
}

export type FieldName =
  | 'clinicId'
  | 'patientName'
  | 'phoneRaw'
  | 'patientAge'
  | 'source'
  | 'sourceDetail'
  | 'sourceConfidence'
  | 'patientIsNew'
  | 'referringDoctorName'
  | 'selectedContactId';

export type FieldErrorMap = Partial<Record<FieldName, string>>;

export type WalkinSubmitResult =
  | { kind: 'success'; patientName: string; walkinId: string }
  | { kind: 'validation'; fieldErrors: FieldErrorMap; orphans: string[] }
  | { kind: 'auth' }
  | { kind: 'badRequest'; message: string }
  | { kind: 'serverError' }
  | { kind: 'network' }
  | { kind: 'unknown' };

/**
 * One contact match returned by POST /api/walkins/lookup.
 *
 * Org-scoped per backend: every match belongs to the same
 * organization as the submitted clinicId. `sameClinic` flags
 * whether the match's clinic equals the one the receptionist
 * has selected for today's visit. With the v1 spine the matched
 * contact may have NO assigned clinic yet (clinicId null), in
 * which case sameClinic is false.
 *
 * Admin-only fields. Never includes phone_fingerprint, raw phone,
 * encrypted phone, diagnosis, treatment, prescription, or notes
 * — Two-Channel Rule.
 */
export interface WalkinLookupMatch {
  contactId: number;
  patientName: string;
  age: number | null;
  /** NULL when the contact has not been resolved to a clinic yet. */
  clinicId: number | null;
  /** NULL when clinicId is null or the clinic name is unknown. */
  clinicName: string | null;
  sameClinic: boolean;
}

/**
 * One ACTIVE (case_status='Open') lead match returned by
 * POST /api/walkins/lookup (S2-P5A2 walk-in-as-lead-source).
 *
 * Org-scoped per backend. One phone can match MANY open leads, so the
 * UI renders all of them as a human-pick list and NEVER auto-selects.
 * This slice is display-only: there is no link/continue action yet.
 *
 * Admin-only fields. Never includes phone, fingerprint, or any
 * clinical/doctor/visit-detail field - Two-Channel Rule.
 */
export interface WalkinLeadMatch {
  leadId: number;
  /** Admin-plane lead name; '' when not captured. */
  leadName: string;
  /** Controlled source token (e.g. 'walk_in', 'paid_search'). */
  source: string;
  /** 'Open' (the backend filters to active leads). */
  caseStatus: string;
  /** ISO timestamp of lead creation; null when unknown. */
  createdAt: string | null;
  /** NULL when the lead has no assigned clinic yet. */
  clinicId: number | null;
  /** NULL when clinicId is null or the clinic name is unknown. */
  clinicName: string | null;
  sameClinic: boolean;
}

/**
 * Prior AdoraCare activity for the matched person, computed fresh from
 * walkins + appointment_events on the backend. Counts only - never any
 * clinical, doctor, or visit-detail content.
 */
export interface WalkinActivity {
  walkinCount: number;
  checkinCount: number;
  /** ISO timestamp of the most recent walk-in/check-in; null when none. */
  lastActivityAt: string | null;
}

/**
 * GUARD 1 (S2-P5E-B2 same-day duplicate). Present when the matched single
 * contact already has a walk-in TODAY (IST). The UI surfaces it as a
 * warn-with-override confirmation gate before registering a second same-day
 * walk-in; a repeat visit is legitimate, so the override is allowed. Admin-only:
 * the timestamp only, never a phone or clinical field.
 */
export interface WalkinSameDayWalkin {
  /** ISO timestamp (walked_in_at) of the existing same-day walk-in. */
  walkedInAt: string;
}

/**
 * Discriminated union returned by lookupWalkinPatient.
 *
 * 'success.found' is true when matches.length > 0; the field is
 * mirrored from the server so the UI does not have to recompute
 * it (and the server's interpretation stays authoritative).
 *
 * 'success.leadMatches' / 'success.activity' are additive S2-P5A2
 * fields; older shapes (and degraded backend responses) parse to an
 * empty array / null respectively.
 *
 * 'rateLimited.message' is the server-provided text when present,
 * otherwise a generic fallback. The UI shows this verbatim.
 */
export type WalkinLookupResult =
  | {
      kind: 'success';
      found: boolean;
      truncated: boolean;
      matches: WalkinLookupMatch[];
      leadMatches: WalkinLeadMatch[];
      leadMatchesTruncated: boolean;
      activity: WalkinActivity | null;
      /**
       * GUARD 1: present when the matched single contact already walked in
       * TODAY (IST). Additive - absent/old responses parse to null, so callers
       * can always read the field safely.
       */
      sameDayWalkin: WalkinSameDayWalkin | null;
    }
  | { kind: 'validation'; fieldErrors: FieldErrorMap; orphans: string[] }
  | { kind: 'auth' }
  | { kind: 'rateLimited'; message: string }
  | { kind: 'serverError' }
  | { kind: 'network' }
  | { kind: 'unknown' };

export interface RawWalkinValues {
  clinicId: string;
  patientName: string;
  phoneRaw: string;
  patientAge: string;
  /** Source tile selected; empty string means none selected. */
  sourceTile: SourceTileId | '';
  /** Free-text for tiles that need it (Referral, Other). */
  sourceDetail: string;
  patientIsNew: NewPatientStatus;
}

// --- Walk-in -> CRM lead create/link (S2-P5A3-BUILD-01) -----------
//
// All gated behind WALKIN_LEAD_LINK_V1_ENABLED (frontend/src/walkin/flags.ts).
// The reason-for-visit picker is a UI helper ONLY: it maps to an admin
// RequirementBucket on the CLIENT so the raw reason word (e.g. "pain") is
// NEVER sent to the server or stored (Two-Channel Rule -- only the
// administrative bucket reaches the backend).

/**
 * Administrative requirement bucket (mirrors the backend RequirementBucket
 * / leads.requirement_bucket CHECK). NOT a clinical record -- a billing /
 * requirement bucket the lead contract already uses.
 */
export type RequirementBucket = 'Dental' | 'Skin' | 'Hair' | 'Pacome' | 'Other';

/**
 * Lightweight reason-for-visit options for the create flow. UI helper only;
 * mapped to a RequirementBucket via REASON_TO_TREATMENT_TYPE before send.
 */
export type ReasonForVisit =
  | 'consultation'
  | 'cleaning'
  | 'pain'
  | 'braces'
  | 'other';

/**
 * Reason -> administrative bucket map (client-side). The pilot clinics are
 * dental, so the clinical reasons project onto the 'Dental' bucket and the
 * generic option onto 'Other'. This projection lives in the walk-in UI
 * layer (not core business logic); only the resulting bucket is persisted,
 * and the receptionist always picks explicitly (treatmentType is collected,
 * never silently defaulted).
 */
export const REASON_TO_TREATMENT_TYPE: Record<ReasonForVisit, RequirementBucket> = {
  consultation: 'Dental',
  cleaning: 'Dental',
  pain: 'Dental',
  braces: 'Dental',
  other: 'Other',
};

export const REASON_OPTIONS: ReadonlyArray<{ id: ReasonForVisit; label: string }> = [
  { id: 'consultation', label: 'Consultation' },
  { id: 'cleaning', label: 'Cleaning' },
  { id: 'pain', label: 'Pain' },
  { id: 'braces', label: 'Braces' },
  { id: 'other', label: 'Other' },
];

/**
 * Lead journey-source vocabulary (mirrors the backend NormalizedSource /
 * leads.source_normalized CHECK). Defaults to 'walk_in' in the create flow;
 * the receptionist may override. This is the LEAD's source -- distinct from
 * the walk-in visit source picked via the form tiles.
 */
export type NormalizedLeadSource =
  | 'paid_search'
  | 'paid_social'
  | 'organic_search'
  | 'directory'
  | 'social_organic'
  | 'offline'
  | 'walk_in'
  | 'corporate'
  | 'insurance'
  | 'referral'
  | 'campaign'
  | 'other';

export const LEAD_SOURCE_OPTIONS: ReadonlyArray<{
  id: NormalizedLeadSource;
  label: string;
}> = [
  { id: 'walk_in', label: 'Walk-in' },
  { id: 'referral', label: 'Referral' },
  { id: 'paid_search', label: 'Paid search' },
  { id: 'paid_social', label: 'Paid social' },
  { id: 'organic_search', label: 'Organic search' },
  { id: 'directory', label: 'Directory' },
  { id: 'social_organic', label: 'Social (organic)' },
  { id: 'offline', label: 'Offline' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'campaign', label: 'Campaign' },
  { id: 'other', label: 'Other' },
];

/**
 * Body for POST /api/walkins/create-lead. Extends the standard walk-in
 * post body (so the server registers the walk-in) with the lead's
 * treatmentType (admin bucket) and optional leadSource override.
 */
export interface WalkinCreateLeadBody extends WalkinPostBody {
  treatmentType: RequirementBucket;
  leadSource?: NormalizedLeadSource;
}

/**
 * Body for POST /api/walkins/link-lead. Extends the standard walk-in post
 * body with the existing lead id the receptionist confirmed.
 */
export interface WalkinLinkLeadBody extends WalkinPostBody {
  targetLeadId: number;
}

/**
 * Discriminated union returned by createWalkinLead / linkWalkinLead.
 * On success the UI navigates to the lead's Lead 360 using `leadId`.
 */
export type WalkinLeadLinkResult =
  | { kind: 'success'; action: 'created' | 'linked'; leadId: number }
  | { kind: 'validation'; fieldErrors: FieldErrorMap; orphans: string[] }
  | { kind: 'auth' }
  | { kind: 'conflict'; message: string }
  | { kind: 'notFound'; message: string }
  | { kind: 'disabled' }
  | { kind: 'rateLimited'; message: string }
  | { kind: 'serverError' }
  | { kind: 'network' }
  | { kind: 'unknown' };
